import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import cn from 'bem-cn';

import { WebAppLayout } from '../../layouts/WebAppLayout';
import { RoutePaths } from '../../constants';
import { Stack, BackgroundImage, InfoBlock } from '../../components/UI';
import { HeaderElements } from '../../components/Header';
import { NavigationElements } from '../../components/Navigation';
import { PromptInfo, PromptInfoSkeleton } from '../../components/PromptInfo';
import { Feed } from '../../components/Feed';
import { copyToClipboard } from '../../utils/clipboard/clipboard';
import { openAliceChat } from '../../utils/alice/alice';
import { isError404 } from '../../utils/error/isError';
import { useLike, useNavigateBack, useSlug, MetrikaGoal, useMetrika } from '../../hooks';
import { useGetPromptQuery, useGetSimilarPromptsQuery } from '../../store';
import { AliceDownloadPopup } from '../../components/AliceDownloadPopup';

import './PromptPage.scss';

const b = cn('prompt-page');

export const PromptPage = () => {
    const { slug: promptId, isValidSlug: isValidPromptId } = useSlug();
    const { reachGoal } = useMetrika();

    const { isFetching, data, isError, error } = useGetPromptQuery({ promptId }, { skip: !isValidPromptId });
    const { isFetching: isSimilarPromptsFetching, data: similarPrompts } = useGetSimilarPromptsQuery(
        { promptId },
        { skip: !data?.id },
    );

    const { likes, isLiked, toggleLike } = useLike({
        promptId,
        initialLikes: data?.likes || 0,
        initialIsLiked: data?.isLiked || false,
    });

    const handleBackButtonClick = useNavigateBack();

    const isMediaPrompt = Boolean(data?.videoData.url);

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                center: { type: 'logo', className: b('mobile-header-logo') },
            },
        }),
        [handleBackButtonClick],
    );

    const navigationElements: NavigationElements = useMemo(() => {
        const handleCopyToClipboard = () => copyToClipboard(data?.prompt);
        const handleOpenAliceChat = () => {
            openAliceChat(data?.id);
            reachGoal(MetrikaGoal.ClickoutChat);
        };

        return {
            desktop: [
                {
                    text: 'Копировать промпт',
                    icon: 'copy',
                    onClick: handleCopyToClipboard,
                    usePopover: true,
                    popoverContent: 'Промпт скопирован',
                },
                {
                    text: isMediaPrompt ? 'Скачать Алиса AI' : 'В чат с Алисой',
                    icon: 'alice',
                    accent: true,
                    onClick: isMediaPrompt ? undefined : handleOpenAliceChat,
                    usePopover: isMediaPrompt,
                    popoverContent: isMediaPrompt ? <AliceDownloadPopup /> : undefined,
                    popoverClassName: b('alice-download-popover'),
                    autoClosePopover: false,
                },
            ],
            mobile: [
                {
                    text: 'Копировать',
                    icon: 'copy',
                    className: b('mobile-nav-button'),
                    onClick: handleCopyToClipboard,
                    usePopover: true,
                    popoverContent: 'Промпт скопирован',
                },
                {
                    text: 'В чат',
                    icon: 'alice',
                    className: b('mobile-nav-button'),
                    onClick: handleOpenAliceChat,
                    accent: true,
                },
            ],
        };
    }, [data?.id, data?.prompt, isMediaPrompt, reachGoal]);

    if (!isValidPromptId || (!isFetching && isError404(error))) {
        return <Navigate to={RoutePaths.NotFound} replace />;
    }

    return (
        <WebAppLayout headerElements={headerElements} navigationElements={navigationElements}>
            <Stack spacing={40} className={b()}>
                <BackgroundImage
                    className={b('background-image')}
                    image={data?.imageData?.urlOptimized}
                    mainColor={data?.imageData?.colors?.MainColor}
                />
                {isFetching && <PromptInfoSkeleton />}
                {!isFetching && isError && !isError404(error) && (
                    <InfoBlock text="Не удалось загрузить промпт. Попробуйте обновить страницу." isError />
                )}
                {!isFetching && !isError && data && (
                    <PromptInfo
                        prompt={{ ...data, likes, isLiked }}
                        onLikeClick={toggleLike}
                        isMediaPrompt={isMediaPrompt}
                    />
                )}
                <Stack spacing={16}>
                    {(isSimilarPromptsFetching || Boolean(similarPrompts?.length)) && (
                        <h2 className={b('similar-title')}>Похожее</h2>
                    )}
                    <Feed prompts={similarPrompts} isLoading={isSimilarPromptsFetching} />
                </Stack>
            </Stack>
        </WebAppLayout>
    );
};
